import * as React from "react";
import { graphql } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import { Media } from "../components/Media";
import Seo from "../components/Seo";
import AussieOwned from "../components/seals/AustralianOwned";
import Vegetarian from "../components/seals/VegetarianFriendly";
import GlutenFree from "../components/seals/GlutenFree";
import StickyWrapper from "../components/StickyWrapper";
import CustomerQuote from "../components/CustomerQuote";
import ogImage from "../images/adrenoIgnite/adreno-ignite-yoga.jpg";
import "../styles/chillOut.css";

const AdrenoIgnite = ({ data }) => {
  const product = getImage(data.product);
  const products = getImage(data.products);
  const busyMum = getImage(data.busyMum);
  const glands = getImage(data.glands);
  const fatigue = getImage(data.fatigue);

  return (
    <>
      <Seo
        title="Adreno Ignite™: Stress, Energy &amp; Stamina Support"
        description="A combination of natural herbs and B vitamins traditionally used in Western medicine to support energy levels and a healthy stress response. Made in Australia."
        image={`https://www.ithrivenutrition.com.au${ogImage}`}
        alt="Adreno Ignite bottle in front of a woman doing yoga."
        handle="adreno-ignite"
      />
      <div className="bg-gray-darker">
        <div className="max-w-screen-lg m-auto pt-2 md:pt-6 lg:pt-10">
          <StickyWrapper
            title="Adreno Ignite"
            subtitle="Stress Support, Energy &amp; Stamina With KSM-66"
            shopLink="adreno-ignite"
          />
          <div className="flex flex-wrap-reverse md:flex-nowrap">
            <div className="text-white mt-0 md:mt-10 w-full px-6 md:p-0 md:w-5/12">
              <p>
                <strong>Adreno Ignite™</strong> combines adaptogenic herbs, B
                vitamins and the world’s most clinically studied Ashwagandha
                (Withania). Traditionally used in Western herbal medicine to
                assist with:
              </p>
              <ul className="list-inside mt-2">
                <li className="list-circle white">
                  Adapting to exhaustion associated with physical, mental and
                  emotional stress
                </li>
                <li className="list-circle white">Promoting stamina</li>
                <li className="list-circle white">Supporting energy levels</li>
                <li className="list-circle white">
                  Supporting a healthy stress response
                </li>
              </ul>

              <div className="flex justify-center my-10">
                <AussieOwned fill="#FFBA09" />
                <Vegetarian fill="#FFBA09" />
                <GlutenFree fill="#FFBA09" />
              </div>
            </div>
            <div className="w-full text-right -mb-8 md:mb-0 md:w-7/12 relative">
              <Media lessThan="md">
                <GatsbyImage
                  image={product}
                  alt="You Beauty bottle"
                  className="bottle-hero"
                />
              </Media>
              <Media greaterThanOrEqual="md">
                <GatsbyImage
                  image={products}
                  alt="You Beauty bottles"
                  className="bottles-hero"
                  style={{ position: "absolute" }}
                />
              </Media>
            </div>
          </div>
        </div>
      </div>
      <div className="yellow-gradient"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 relative overflow-hidden">
        <div className="h-96 md:h-full" style={{ maxHeight: "550px" }}>
          <BgImage
            image={busyMum}
            style={{ height: "100%", backgroundPosition: "65% 40%" }}
          />
        </div>
        <div className="px-8 py-12 md:py-24 lg:py-36 xl:py-44 2xl:py-48">
          <h2 className="text-40 text-center mb-4 text-yellow-dark">
            Better Balance.
          </h2>
          <p className="max-w-md mx-auto leading-loose">
            Adreno Ignite™ is formulated for busy people balancing a full load
            of responsibilities. If you find yourself regularly stressed or
            exhausted, Adreno Ignite™ is for you.
          </p>
        </div>
      </div>
      <div className="bg-black">
        <div className="max-w-screen-xl xl:m-auto grid grid-cols-1 md:grid-cols-2 relative overflow-hidden items-center">
          <div className="py-12">
            <h2
              className="text-35 md:text-40 text-center mb-4"
              style={{ color: "#A2CFF0" }}
            >
              Meet Your Adrenals.
            </h2>
            <p className="text-white px-8 max-w-md mx-auto leading-loose">
              Your adrenals are two small glands located on top of your kidneys
              that secrete stress hormones, such as cortisol, adrenaline and
              noradrenaline – All of which are used to prepare your body for a
              fight or flight response.
            </p>
          </div>
          <div style={{ maxHeight: "450px" }}>
            <GatsbyImage
              image={glands}
              alt="3D render of adrenal glands located on the kidneys"
            />
          </div>
        </div>
      </div>
      <div style={{ backgroundColor: "#131313" }}>
        <div className="max-w-screen-xl xl:m-auto grid grid-cols-1 md:grid-cols-2 relative overflow-hidden items-center">
          <div className="py-12 md:order-2">
            <h2
              className="text-35 md:text-40 text-center mb-4"
              style={{ color: "#E55B48" }}
            >
              Adrenal Fatigue
            </h2>
            <p className="text-white px-8 max-w-prose mx-auto leading-loose pb-4">
              Adrenal fatigue occurs when the adrenal glands are unable to keep
              up the pace needed, to secrete stress hormones on demand. As a
              result, they can't produce quite enough of the hormones you need
              to feel good. This can result in symptoms like exhaustion,
              weakened immunity, sleep disturbances, and food cravings.
            </p>
            <p className="text-white px-8 max-w-prose mx-auto leading-loose">
              The root cause of adrenal fatigue can be mental, spiritual,
              emotional, chemical or dietary imbalances.
            </p>
          </div>
          <div className="self-end">
            <GatsbyImage
              image={fatigue}
              alt="3D render of adrenal glands located on the kidneys"
              className="max-w-xl mx-auto"
            />
          </div>
        </div>
      </div>
      <div className="bg-gray-dark p-6">
        <h2 className="text-35 md:text-40 text-white text-center">
          Results That Speak For Themselves.
        </h2>
        <div className="flex flex-col md:flex-row md:justify-center items-center">
          <CustomerQuote name="Rahul" color="yellow-dark" isRight={true}>
            <p>
              This is the perfect product for people with 9-5 jobs! I take it
              daily and it has really supported my health.
            </p>
          </CustomerQuote>
          <CustomerQuote name="Teresa" color="yellow-dark" isRight={false}>
            <p>
              This will change your life !!!
              <br />
              I’m so grateful for adreno ignite. Being a busy coffee shop owner
              it has really support my health. Have tried other products. But
              this is the best so far.
            </p>
          </CustomerQuote>
        </div>
      </div>
    </>
  );
};

export default AdrenoIgnite;

export const query = graphql`
  query {
    product: file(
      relativePath: { eq: "adrenoIgnite/adreno-ignite-bottle.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    products: file(
      relativePath: { eq: "adrenoIgnite/adreno-ignite-bottles.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    busyMum: file(relativePath: { eq: "adrenoIgnite/busy-mum.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    glands: file(relativePath: { eq: "adrenoIgnite/adrenal-glands.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    fatigue: file(relativePath: { eq: "adrenoIgnite/adrenal-fatigue.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
  }
`;
